<template>
    <div class="targets">
        <div class="targets__header">
            <h2 class="targets__title">Program Targets</h2>
            <p class="targets__subtitle">
                Your shaping plan — break each target word into steps from current ability to final goal.
            </p>
        </div>

        <!-- No echoic targets indicated: the Guide's alternative, quoted -->
        <NoTargetsPanel v-if="noTargets" />

        <!-- Hero generate CTA -->
        <div class="generate-hero" :class="{ 'generate-hero--ready': miniPlanWords.length > 0 }">
            <div class="generate-hero__content">
                <div class="generate-hero__icon">
                    <span class="material-symbols-outlined">auto_awesome</span>
                </div>
                <div class="generate-hero__text">
                    <h3>Generate targets from your word ideas</h3>
                    <p v-if="miniPlanWords.length > 0">
                        The AI will turn your {{ miniPlanWords.length }} word{{ miniPlanWords.length === 1 ? '' : 's' }}
                        into full shaping ladders — end word, intermediate steps, and starting sound.
                    </p>
                    <p v-else>
                        Go back to <strong>Build Program</strong> and generate some word ideas first.
                    </p>
                </div>
            </div>
            <button
                type="button"
                class="btn btn--primary btn--hero"
                :disabled="generating || miniPlanWords.length === 0"
                @click="generateFromMiniPlan"
            >
                <span class="material-symbols-outlined">auto_awesome</span>
                <span v-if="generating">Generating...</span>
                <span v-else-if="targets.length > 0 && targets.some(t => t.header)">Generate more</span>
                <span v-else>Generate Targets</span>
            </button>
        </div>

        <!-- Example card -->
        <details class="example" open>
            <summary class="example__summary">
                <span class="material-symbols-outlined">lightbulb</span>
                How a program target looks (example: "movie")
            </summary>
            <div class="example__card target-card target-card--example">
                <div class="target-card__head">
                    <span class="target-card__badge">Example</span>
                    <input type="text" value="movie" readonly disabled class="target-card__word" />
                </div>
                <div class="target-card__body">
                    <div class="step step--end">
                        <span class="step__label">End</span>
                        <input type="text" value="moo-vee" readonly disabled />
                    </div>
                    <div class="step step--mid">
                        <span class="step__label">...</span>
                        <input type="text" value="moo-ee" readonly disabled />
                    </div>
                    <div class="step step--mid">
                        <span class="step__label">...</span>
                        <input type="text" value="oo-ee" readonly disabled />
                    </div>
                    <div class="step step--start">
                        <span class="step__label">Start</span>
                        <input type="text" value="oo" readonly disabled />
                    </div>
                </div>
            </div>
        </details>

        <!-- Split layout: targets on the left, tips + actions on the right -->
        <div class="targets__split">
        <div class="targets__main">
        <!-- Targets grid (2-col on wide screens) -->
        <div class="targets__list">
            <div
                v-for="(target, tIdx) in targets"
                :key="tIdx"
                class="target-card"
            >
                <div class="target-card__head">
                    <span class="target-card__badge">T{{ tIdx + 1 }}</span>
                    <input
                        type="text"
                        v-model="target.header"
                        placeholder="Target word (e.g., teddy)"
                        class="target-card__word"
                    />
                    <button
                        type="button"
                        class="target-card__remove"
                        @click="removeTarget(tIdx)"
                        title="Remove target"
                    >
                        <span class="material-symbols-outlined">delete</span>
                    </button>
                </div>
                <div class="target-card__body">
                    <div
                        v-for="(input, iIdx) in target.inputs"
                        :key="iIdx"
                        class="step"
                        :class="`step--${stepKey(input)}`"
                    >
                        <span class="step__label">{{ stepKey(input) === 'mid' ? '...' : stepKey(input) }}</span>
                        <input
                            type="text"
                            :value="input[stepKey(input) === 'mid' ? '...' : stepKey(input)]"
                            @input="updateStepValue(tIdx, iIdx, $event.target.value)"
                            :placeholder="stepPlaceholder(stepKey(input))"
                        />
                        <button
                            v-if="stepKey(input) === 'mid' && countMidSteps(target) > 1"
                            type="button"
                            class="step__remove"
                            @click="removeStep(tIdx, iIdx)"
                            title="Remove step"
                        >
                            <span class="material-symbols-outlined">close</span>
                        </button>
                    </div>
                    <button
                        v-if="countMidSteps(target) < 5"
                        type="button"
                        class="step-add"
                        @click="addStep(tIdx)"
                    >
                        <span class="material-symbols-outlined">add</span>
                        Add step
                    </button>
                </div>
            </div>

            <button
                type="button"
                class="target-add"
                @click="addTarget"
            >
                <span class="material-symbols-outlined">add_circle</span>
                <span>Add another target</span>
            </button>
        </div>

        <!-- Inline clear button under the targets grid -->
        <div class="targets__inline-actions" v-if="targets.length > 0 && targets.some(t => t.header)">
            <button
                type="button"
                class="btn btn--ghost"
                @click="clearAll"
            >
                <span class="material-symbols-outlined">refresh</span>
                Clear all targets
            </button>
        </div>
        </div>

        <!-- Right sidebar: stats, tips, actions -->
        <aside class="targets__sidebar">
            <!-- Quick stats card -->
            <div class="sidebar-card sidebar-card--stats">
                <div class="stat">
                    <div class="stat__value">{{ filledTargetCount }}</div>
                    <div class="stat__label">Target{{ filledTargetCount === 1 ? '' : 's' }} ready</div>
                </div>
                <div class="stat stat--secondary">
                    <div class="stat__value">{{ totalSteps }}</div>
                    <div class="stat__label">Total shaping steps</div>
                </div>
            </div>

            <!-- Session tips card -->
            <div class="sidebar-card">
                <h3 class="sidebar-card__title">
                    <span class="material-symbols-outlined">tips_and_updates</span>
                    Using these in session
                </h3>
                <ul class="tips">
                    <li class="tip">
                        <span class="tip__num">1</span>
                        <div>
                            <strong>Start at the bottom.</strong>
                            Reinforce the <em>Start</em> sound first — the simplest approximation.
                        </div>
                    </li>
                    <li class="tip">
                        <span class="tip__num">2</span>
                        <div>
                            <strong>Build up gradually.</strong>
                            Once a step is consistent, prompt the next one up. Don't skip steps.
                        </div>
                    </li>
                    <li class="tip">
                        <span class="tip__num">3</span>
                        <div>
                            <strong>Aim for 80%.</strong>
                            Move up a step only after ~80% accuracy over 2+ sessions.
                        </div>
                    </li>
                    <li class="tip">
                        <span class="tip__num">4</span>
                        <div>
                            <strong>Pair with preferences.</strong>
                            Follow each correct vocal with something the learner enjoys.
                        </div>
                    </li>
                </ul>
            </div>

            <!-- Actions card (sticky on desktop) -->
            <div class="sidebar-card sidebar-card--actions">
                <button
                    type="button"
                    class="btn btn--primary btn--wide"
                    @click="saveTargets"
                    :disabled="saving"
                >
                    <span v-if="saving">Saving...</span>
                    <span v-else>
                        <span class="material-symbols-outlined">save</span>
                        Save Program
                    </span>
                </button>
                <GuidedTargetsPdf
                    :assessmentId="assessmentId"
                    :assessment="localAssessment || assessment"
                    :targets="targets"
                />
                <p class="sidebar-card__hint">
                    Download a one-page PDF — perfect to bring into a session.
                </p>
            </div>
        </aside>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import api from '@/services/axios';
import { handleError } from '@/utils/handleError';
import { aiFeaturesAllowed } from '@/utils/legal-gate';
import { showDialog, showToast, showConfirmDialog } from '@/utils/swal';
import GuidedTargetsPdf from '@/components/assesments/guided/GuidedTargetsPdf.vue';
import NoTargetsPanel from '@/components/assesments/NoTargetsPanel.vue';
import { noTargetsIndicated } from '@/utils/assessment-targets';

const props = defineProps({
    organizationId: Number,
    learnerId: Number,
    assessmentId: Number,
    assessment: Object,
    canConductAssessment: Boolean,
});

const targets = ref([]);
const miniPlanWords = ref([]);
const generating = ref(false);
const saving = ref(false);
const localAssessment = ref(null);

const filledTargetCount = computed(() =>
    targets.value.filter(t => t.header && t.header.trim()).length
);

// Group scores as percentages of 1-point items, same shape the decision box uses
const groupScoresPct = computed(() => {
    const raw = (localAssessment.value || props.assessment)?.result?.group_scores || {};
    const out = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    for (let i = 1; i <= 5; i++) {
        const g = raw[i];
        if (g?.total > 0) out[i] = (g.score_one / g.total) * 100;
    }
    return out;
});

const noTargets = computed(() => noTargetsIndicated(groupScoresPct.value));

const totalSteps = computed(() =>
    targets.value
        .filter(t => t.header && t.header.trim())
        .reduce((sum, t) => sum + t.inputs.length, 0)
);

const emptyTarget = () => ({
    header: '',
    inputs: [
        { end: '' },
        { '...': '' },
        { start: '' },
    ],
});

const loadFromAssessment = async () => {
    try {
        const { data } = await api.get(
            `/organizations/${props.organizationId}/assessments/${props.assessmentId}`
        );
        localAssessment.value = data?.data || null;
        const noteData = data?.data?.note;

        if (noteData?.program_targets) {
            try {
                const parsed = JSON.parse(noteData.program_targets);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    targets.value = parsed;
                } else {
                    targets.value = [emptyTarget()];
                }
            } catch (e) {
                targets.value = [emptyTarget()];
            }
        } else {
            targets.value = [emptyTarget()];
        }

        if (noteData?.mini_program_plan) {
            try {
                miniPlanWords.value = JSON.parse(noteData.mini_program_plan) || [];
            } catch (e) {
                miniPlanWords.value = [];
            }
        }
    } catch (error) {
        handleError(error);
    }
};

const stepKey = (input) => {
    const key = Object.keys(input)[0];
    if (key === 'end') return 'end';
    if (key === 'start') return 'start';
    return 'mid';
};

const stepPlaceholder = (kind) => ({
    end: 'final full word, e.g. moo-vee',
    start: 'starting sound, e.g. oo',
    mid: 'intermediate step',
}[kind]);

const countMidSteps = (target) => target.inputs.filter(i => stepKey(i) === 'mid').length;

const updateStepValue = (tIdx, iIdx, value) => {
    const input = targets.value[tIdx].inputs[iIdx];
    const key = Object.keys(input)[0];
    input[key] = value;
};

const addStep = (tIdx) => {
    const inputs = targets.value[tIdx].inputs;
    const endIdx = inputs.findIndex(i => 'end' in i);
    inputs.splice(endIdx + 1, 0, { '...': '' });
};

const removeStep = (tIdx, iIdx) => {
    targets.value[tIdx].inputs.splice(iIdx, 1);
};

const addTarget = () => {
    targets.value.push(emptyTarget());
};

const removeTarget = (tIdx) => {
    targets.value.splice(tIdx, 1);
    if (targets.value.length === 0) {
        targets.value = [emptyTarget()];
    }
};

const clearAll = async () => {
    const result = await showConfirmDialog({
        title: 'Clear all targets?',
        html: 'This will remove all program targets. You can rebuild them from your word ideas.',
        confirmButtonText: 'Clear',
        cancelButtonText: 'Keep',
        icon: 'warning',
    });
    if (result.isConfirmed) {
        targets.value = [emptyTarget()];
    }
};

const generateFromMiniPlan = async () => {
    if (miniPlanWords.value.length === 0) return;
    if (!aiFeaturesAllowed()) return;

    const existingHeaders = targets.value.map(t => t.header?.toLowerCase().trim()).filter(Boolean);
    const newWords = miniPlanWords.value.filter(
        w => w.word && !existingHeaders.includes(w.word.toLowerCase().trim())
    );

    if (newWords.length === 0) {
        showToast({ icon: 'info', text: 'All word ideas are already in your targets' });
        return;
    }

    const slotsAvailable = Math.max(0, 8 - existingHeaders.length);
    const wordsToProcess = newWords.slice(0, slotsAvailable);

    if (wordsToProcess.length === 0) {
        showDialog({
            icon: 'info',
            title: 'Max 8 targets reached',
            html: 'Remove some targets first if you want to add more.',
        });
        return;
    }

    generating.value = true;
    try {
        const { data } = await api.post('/open-ai/program-targets/generate', {
            words: wordsToProcess.map(w => ({
                word: w.word,
                ipa: w.ipa || null,
                consonants: w.consonants || null,
                vowels: w.vowels || null,
            })),
        });

        if (data.results?.length > 0) {
            const firstEmpty = targets.value.length === 1
                && !targets.value[0].header?.trim();
            if (firstEmpty) {
                targets.value = data.results;
            } else {
                targets.value = [...targets.value, ...data.results];
            }
            showToast({ icon: 'success', text: `Generated ${data.results.length} target(s)` });
        }
    } catch (error) {
        handleError(error);
    } finally {
        generating.value = false;
    }
};

const saveTargets = async () => {
    if (!props.canConductAssessment) {
        showDialog({ icon: 'warning', html: "You don't have permission to perform this action." });
        return;
    }
    saving.value = true;
    try {
        const output = targets.value
            .filter(t => t.header?.trim())
            .map(target => ({
                header: target.header,
                inputs: target.inputs.map(input => {
                    const key = Object.keys(input)[0];
                    return { [key]: input[key] || null };
                }),
            }));

        await api.post(
            `/organizations/${props.organizationId}/assessments/${props.assessmentId}/notes`,
            { program_targets: JSON.stringify(output) }
        );
        showToast({ icon: 'success', text: 'Program saved' });
    } catch (error) {
        handleError(error);
    } finally {
        saving.value = false;
    }
};

onMounted(() => {
    loadFromAssessment();
});
</script>

<style scoped>
.targets {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.targets__header h2 {
    font-size: 24px;
    font-weight: 700;
    margin: 0;
    color: #222;
}

.targets__subtitle {
    color: #666;
    margin: 4px 0 0;
    font-size: 15px;
}

/* Hero generate */
.generate-hero {
    background: linear-gradient(135deg, #725CFF 0%, #9d8bff 100%);
    color: #fff;
    border-radius: 14px;
    padding: 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    flex-wrap: wrap;
    box-shadow: 0 6px 20px rgba(114, 92, 255, 0.25);
}

.generate-hero--ready {
    animation: shimmer 3s ease-in-out infinite;
}

@keyframes shimmer {
    0%, 100% { box-shadow: 0 6px 20px rgba(114, 92, 255, 0.25); }
    50% { box-shadow: 0 6px 28px rgba(114, 92, 255, 0.45); }
}

.generate-hero__content {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
    min-width: 250px;
}

.generate-hero__icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    backdrop-filter: blur(6px);
}

.generate-hero__icon .material-symbols-outlined {
    font-size: 26px;
}

.generate-hero__text h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 4px;
}

.generate-hero__text p {
    margin: 0;
    font-size: 13px;
    opacity: 0.9;
    line-height: 1.4;
}

.btn--hero {
    background: #fff;
    color: #725CFF;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    padding: 12px 20px;
    font-weight: 600;
    flex-shrink: 0;
}

.btn--hero:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
}

.btn--hero:disabled {
    opacity: 0.7;
}

/* Example disclosure */
.example {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(114, 92, 255, 0.06);
    overflow: hidden;
}

.example__summary {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 20px;
    font-size: 14px;
    color: #725CFF;
    font-weight: 500;
    cursor: pointer;
    list-style: none;
}

.example__summary::-webkit-details-marker { display: none; }

.example__summary:hover {
    background: #f7f6ff;
}

.example__card {
    margin: 0 20px 20px;
}

/* Target card */
.targets__list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.target-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(114, 92, 255, 0.06);
    overflow: hidden;
    border: 1px solid transparent;
    transition: border-color 0.2s;
}

.target-card:hover:not(.target-card--example) {
    border-color: #e4dfff;
}

.target-card--example {
    opacity: 0.85;
}

.target-card__head {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 18px;
    background: #f7f6ff;
    border-bottom: 1px solid #ededf7;
}

.target-card__badge {
    background: #725CFF;
    color: #fff;
    font-weight: 700;
    font-size: 13px;
    padding: 4px 10px;
    border-radius: 6px;
    flex-shrink: 0;
}

.target-card--example .target-card__badge {
    background: #888;
}

.target-card__word {
    flex: 1;
    border: none;
    background: transparent;
    font-size: 16px;
    font-weight: 600;
    color: #222;
    padding: 6px 0;
}

.target-card__word:focus {
    outline: none;
}

.target-card__word:disabled {
    color: #444;
}

.target-card__remove {
    background: transparent;
    border: none;
    color: #bbb;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.target-card__remove:hover {
    background: #ffeded;
    color: #c44;
}

.target-card__remove .material-symbols-outlined {
    font-size: 18px;
}

.target-card__body {
    padding: 16px 18px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.step {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #fafaff;
    border-radius: 8px;
    padding: 8px 12px;
    border-left: 3px solid #e4dfff;
}

.step--end {
    border-left-color: #36C27A;
    background: #f0f9f4;
}

.step--start {
    border-left-color: #725CFF;
    background: #f7f6ff;
}

.step__label {
    min-width: 40px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #888;
    flex-shrink: 0;
}

.step--end .step__label { color: #36C27A; }
.step--start .step__label { color: #725CFF; }

.step input {
    flex: 1;
    border: none;
    background: transparent;
    font-size: 14px;
    color: #222;
    padding: 4px 0;
}

.step input:focus {
    outline: none;
}

.step input:disabled {
    color: #444;
}

.step__remove {
    background: transparent;
    border: none;
    color: #bbb;
    cursor: pointer;
    padding: 2px;
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.step__remove:hover {
    background: #ffeded;
    color: #c44;
}

.step__remove .material-symbols-outlined {
    font-size: 14px;
}

.step-add {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: transparent;
    border: 1px dashed #d4cfff;
    color: #725CFF;
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    align-self: flex-start;
    margin-top: 4px;
}

.step-add:hover {
    background: #f7f6ff;
    border-color: #725CFF;
}

.step-add .material-symbols-outlined {
    font-size: 16px;
}

/* Add target button */
.target-add {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: #fff;
    border: 2px dashed #d4cfff;
    color: #725CFF;
    padding: 20px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
}

.target-add:hover {
    background: #f7f6ff;
    border-color: #725CFF;
}

.target-add .material-symbols-outlined {
    font-size: 22px;
}

/* Split layout: targets + sidebar */
.targets__split {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 320px;
    gap: 20px;
    align-items: start;
}

@media (max-width: 1024px) {
    .targets__split {
        grid-template-columns: 1fr;
    }
}

.targets__main {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.targets__sidebar {
    display: flex;
    flex-direction: column;
    gap: 14px;
    position: sticky;
    top: 24px;
}

@media (max-width: 1024px) {
    .targets__sidebar {
        position: static;
    }
}

/* Target grid becomes 2-col on wide */
.targets__list {
    display: grid;
    grid-template-columns: 1fr;
    gap: 14px;
}

@media (min-width: 1280px) {
    .targets__list {
        grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
    }
}

.target-add {
    grid-column: 1 / -1;
}

.targets__inline-actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 4px;
}

/* Sidebar cards */
.sidebar-card {
    background: #fff;
    border-radius: 12px;
    padding: 18px;
    box-shadow: 0 2px 8px rgba(114, 92, 255, 0.06);
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.sidebar-card__title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #222;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.sidebar-card__title .material-symbols-outlined {
    font-size: 20px;
    color: #725CFF;
}

.sidebar-card__hint {
    font-size: 12px;
    color: #888;
    margin: 0;
    line-height: 1.4;
    text-align: center;
}

/* Stats card */
.sidebar-card--stats {
    flex-direction: row;
    background: linear-gradient(135deg, #725CFF 0%, #9d8bff 100%);
    color: #fff;
    padding: 16px;
    justify-content: space-around;
}

.stat {
    text-align: center;
    flex: 1;
}

.stat__value {
    font-size: 28px;
    font-weight: 700;
    line-height: 1;
}

.stat__label {
    font-size: 11px;
    opacity: 0.9;
    margin-top: 4px;
}

.stat--secondary {
    border-left: 1px solid rgba(255, 255, 255, 0.25);
    padding-left: 8px;
}

/* Tips list */
.tips {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.tip {
    display: flex;
    gap: 10px;
    font-size: 13px;
    line-height: 1.45;
    color: #444;
}

.tip strong {
    color: #222;
    display: block;
    margin-bottom: 2px;
}

.tip em {
    color: #725CFF;
    font-style: normal;
    font-weight: 600;
}

.tip__num {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #f0edff;
    color: #725CFF;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Actions card */
.sidebar-card--actions {
    background: #fafaff;
}

.btn--ghost {
    background: transparent;
    color: #888;
    border: 1px solid transparent;
}

.btn--ghost:not(:disabled):hover {
    background: #ffeded;
    color: #c44;
    border-color: #ffd4d4;
}

.btn--wide {
    width: 100%;
}

.btn .material-symbols-outlined {
    font-size: 18px;
}

.btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 18px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: transform 0.1s, box-shadow 0.15s, background 0.15s;
}

.btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn--primary {
    background: #725CFF;
    color: #fff;
    box-shadow: 0 2px 6px rgba(114, 92, 255, 0.25);
}

.btn--primary:not(:disabled):hover {
    background: #5d47e0;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(114, 92, 255, 0.35);
}

.btn--secondary {
    background: #fff;
    color: #725CFF;
    border: 1px solid #e4dfff;
}

.btn--secondary:not(:disabled):hover {
    background: #f7f6ff;
}

.btn .material-symbols-outlined {
    font-size: 18px;
}

@media (max-width: 640px) {
    .generate-hero {
        flex-direction: column;
        align-items: stretch;
    }
    .btn--hero {
        width: 100%;
    }
}
</style>
